import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./LanguagesQuiz.css";
import languagesBadge from "../assets/badges/languagesbadge.png";
import quizResultImg from "../assets/General/quizresult.png";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";
const TOTAL_QUESTIONS = 5;

const LANGUAGE = {
  SINHALA: "sinhala",
  TAMIL: "tamil",
};

const languageContent = {
  phrases: [
    {
      id: "hello",
      label: "Welcome",
      sinhala: { script: "ආයුබෝවන්", roman: "āyubōvan", meaning: "Welcome" },
      tamil: { script: "வணக்கம்", roman: "Vāṉakam", meaning: "Welcome" },
    },
    {
      id: "myNameIs",
      label: "My name is…",
      sinhala: {
        script: "මගේ නම වන්නේ …",
        roman: "Magē nama vannē …",
        meaning: "My name is...",
      },
      tamil: {
        script: "என் பெயர் …",
        roman: "En peyar …",
        meaning: "My name is...",
      },
    },
    {
      id: "howAreYou",
      label: "How are you?",
      sinhala: {
        script: "කොහොමද?",
        roman: "Kohomada?",
        meaning: "How are you?",
      },
      tamil: {
        script: "எப்படி இருக்கிறீர்கள்?",
        roman: "eppaṭi irukkiṟīrkaḷ?",
        meaning: "How are you?",
      },
    },
    {
      id: "doingWell",
      label: "I am good",
      sinhala: { script: "මම හොඳින්", roman: "Mama hŏdin", meaning: "I am fine" },
      tamil: {
        script: "நான் நல்லா இருக்கிறேன்",
        roman: "Nāṉ nallā irukkuṟēṉ",
        meaning: "I am fine",
      },
    },
    {
      id: "goodbye",
      label: "Goodbye",
      sinhala: { script: "ආයුබෝවන්", roman: "āyubōvan", meaning: "Goodbye" },
      tamil: {
        script: "போய்ட்டு வரேன்",
        roman: "Pōyṭṭu varēṉ",
        meaning: "Goodbye",
      },
    },
  ],
  numbers: [
    {
      n: 1,
      sinhala: { script: "එක", roman: "eka", meaning: "one" },
      tamil: { script: "ஒன்று", roman: "Oṉṟu", meaning: "one" },
    },
    {
      n: 2,
      sinhala: { script: "දෙක", roman: "deka", meaning: "two" },
      tamil: { script: "இரண்டு", roman: "Iraṇṭu", meaning: "two" },
    },
    {
      n: 3,
      sinhala: { script: "තුන", roman: "tuna", meaning: "three" },
      tamil: { script: "மூன்று", roman: "Mūṉṟu", meaning: "three" },
    },
    {
      n: 4,
      sinhala: { script: "හතර", roman: "hatara", meaning: "four" },
      tamil: { script: "நான்கு", roman: "Nāṉku", meaning: "four" },
    },
    {
      n: 5,
      sinhala: { script: "පහ", roman: "paha", meaning: "five" },
      tamil: { script: "ஐந்து", roman: "Aintu", meaning: "five" },
    },
    {
      n: 6,
      sinhala: { script: "හය", roman: "haya", meaning: "six" },
      tamil: { script: "ஆறு", roman: "Āṟu", meaning: "six" },
    },
    {
      n: 7,
      sinhala: { script: "හත", roman: "hatha", meaning: "seven" },
      tamil: { script: "ஏழு", roman: "Ēḻu", meaning: "seven" },
    },
    {
      n: 8,
      sinhala: { script: "අට", roman: "aṭa", meaning: "eight" },
      tamil: { script: "எட்டு", roman: "Eṭṭu", meaning: "eight" },
    },
    {
      n: 9,
      sinhala: { script: "නවය", roman: "navaya", meaning: "nine" },
      tamil: { script: "ஒன்பது", roman: "Oṉpatu", meaning: "nine" },
    },
    {
      n: 10,
      sinhala: { script: "දහය", roman: "dahaya", meaning: "ten" },
      tamil: { script: "பத்து", roman: "Pattu", meaning: "ten" },
    },
  ],
};

function shuffle(array) {
  const shuffledItems = [...array];

  // this keeps the questions from showing in the same order every time
  for (let index = shuffledItems.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffledItems[index], shuffledItems[swapIndex]] = [
      shuffledItems[swapIndex],
      shuffledItems[index],
    ];
  }

  return shuffledItems;
}

function getLanguageFolder(language) {
  return language === LANGUAGE.SINHALA ? "sinhala" : "tamil";
}

function buildQuizPool(language) {
  // phrases and numbers are turned into one list so the quiz can mix them
  const phraseItems = languageContent.phrases.map((item) => ({
    key: item.id,
    prompt: item[language].script,
    roman: item[language].roman,
    answer: item[language].meaning,
    audio: `/audio/${getLanguageFolder(language)}/${item.id}.mp3`,
    type: "phrase",
  }));

  const numberItems = languageContent.numbers.map((item) => ({
    key: `num-${item.n}`,
    prompt: item[language].script,
    roman: item[language].roman,
    answer:
      item[language].meaning.charAt(0).toUpperCase() + item[language].meaning.slice(1),
    audio: `/audio/${getLanguageFolder(language)}/num-${item.n}.mp3`,
    type: "number",
  }));

  return [...phraseItems, ...numberItems];
}

function buildQuestions(language) {
  const quizPool = buildQuizPool(language);
  const chosenAnswers = shuffle(quizPool).slice(0, TOTAL_QUESTIONS);

  return chosenAnswers.map((correctItem) => {
    const wrongOptions = shuffle(
      quizPool.filter((item) => item.key !== correctItem.key)
    ).slice(0, 2);

    // the correct answer is mixed with two wrong choices
    const answerOptions = shuffle([
      { key: correctItem.key, title: correctItem.answer },
      ...wrongOptions.map((item) => ({
        key: item.key,
        title: item.answer,
      })),
    ]);

    return {
      id: correctItem.key,
      prompt: correctItem.prompt,
      roman: correctItem.roman,
      audio: correctItem.audio,
      correctKey: correctItem.key,
      correctTitle: correctItem.answer,
      options: answerOptions,
      selectedKey: null,
    };
  });
}

function getCurrentUserName() {
  // the app has used a few possible storage names, so this checks all of them
  const possibleKeys = [
    "exploreLankaUser",
    "exploreLankaUsername",
    "currentUser",
    "user",
    "username",
    "name",
  ];

  for (const key of possibleKeys) {
    const storedValue = localStorage.getItem(key);
    if (!storedValue) continue;

    try {
      const parsedValue = JSON.parse(storedValue);
      if (typeof parsedValue === "string" && parsedValue.trim()) {
        return parsedValue.trim();
      }
      if (parsedValue?.name) return String(parsedValue.name).trim();
      if (parsedValue?.user?.name) return String(parsedValue.user.name).trim();
      if (parsedValue?.username) return String(parsedValue.username).trim();
    } catch {
      if (storedValue.trim()) return storedValue.trim();
    }
  }

  return "";
}

function HomeButton({ goToPage }) {
  return (
    <button
      className="quizHomeBtn"
      onClick={() => goToPage("/loading", { state: { to: "/home" } })}
      aria-label="Go Home"
      title="Go Home"
      type="button"
    >
      <svg
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="quizHomeBtn__icon"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8 0L0 6V8H1V15H4V10H7V15H15V8H16V6L14 4.5V1H11V2.25L8 0ZM9 10H12V13H9V10Z"
          fill="#ffffff"
        />
      </svg>
    </button>
  );
}

export default function LanguagesQuiz() {
  const goToPage = useNavigate();
  const audioRef = useRef(null);

  function playYaySound() {
    try {
      const yaySound = new Audio("/audio/yay.mp3");
      yaySound.volume = 1;
      yaySound.play().catch(() => {});
    } catch (error) {
      console.warn("Yay sound failed:", error);
    }
  }

  function playBadgeSound() {
    try {
      const badgeSound = new Audio("/audio/badge.mp3");
      badgeSound.volume = 1;
      badgeSound.play().catch(() => {});
    } catch (error) {
      console.warn("Badge sound failed:", error);
    }
  }

  useEffect(() => {
    // pause the background music while this quiz is using voice clips
    localStorage.setItem("exploreLankaPauseMusicForVoice", "true");
    window.dispatchEvent(new Event("storage"));

    return () => {
      localStorage.setItem("exploreLankaPauseMusicForVoice", "false");
      window.dispatchEvent(new Event("storage"));
    };
  }, []);

  const [hasStarted, setHasStarted] = useState(false);
  const [quizLanguage, setQuizLanguage] = useState(LANGUAGE.SINHALA);
  const [questions, setQuestions] = useState(() => buildQuestions(LANGUAGE.SINHALA));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedKey, setSelectedKey] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [score, setScore] = useState(0);
  const [showBadgePopup, setShowBadgePopup] = useState(false);
  const [savingResult, setSavingResult] = useState(false);
  const [bestScore, setBestScore] = useState(null);

  // refs help keep the final score correct when the save happens after state changes
  const hasSavedResultRef = useRef(false);
  const scoreRef = useRef(0);
  const answersRef = useRef([]);

  const currentQuestion = questions[currentIndex];
  const isFinished = currentIndex >= questions.length;
  const pageName =
    quizLanguage === LANGUAGE.SINHALA
      ? "languages_quiz_sinhala"
      : "languages_quiz_tamil";

  const progressText = useMemo(() => {
    return `${Math.min(currentIndex + 1, questions.length)} / ${questions.length}`;
  }, [currentIndex, questions.length]);

  useEffect(() => {
    if (hasStarted) return;

    async function fetchBestScore() {
      const playerName = getCurrentUserName();
      if (!playerName) return;

      try {
        const url = `${API_BASE}/api/best-score/${encodeURIComponent(playerName)}/${pageName}`;
        const response = await fetch(url);
        const scoreData = await response.json();

        if (scoreData?.best_score !== null && scoreData?.best_score !== undefined) {
          setBestScore(scoreData.best_score);
        } else {
          setBestScore(null);
        }
      } catch (error) {
        console.warn("[LanguagesQuiz] fetchBestScore error:", error);
      }
    }

    fetchBestScore();
  }, [hasStarted, pageName]);

  function playAudio(src) {
    if (!src) return;

    try {
      // stop the last word first so the new one is easier to hear
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      const wordAudio = new Audio(src);
      audioRef.current = wordAudio;
      wordAudio.preload = "auto";
      wordAudio.play().catch(() => {});
    } catch (error) {
      console.warn("[LanguagesQuiz] audio play failed:", error);
    }
  }

  function resetQuizState() {
    // both start and play again need the same clean quiz setup
    setQuestions(buildQuestions(quizLanguage));
    setCurrentIndex(0);
    setSelectedKey(null);
    setShowFeedback(false);
    setScore(0);
    setShowBadgePopup(false);
    hasSavedResultRef.current = false;
    scoreRef.current = 0;
    answersRef.current = [];
  }

  function startQuiz() {
    resetQuizState();
    setHasStarted(true);
  }

  function handleAnswer(selectedOptionKey) {
    if (showFeedback) return;

    const isCorrect = selectedOptionKey === currentQuestion.correctKey;

    if (isCorrect) {
      scoreRef.current += 1;
      setScore((previousScore) => previousScore + 1);
    }

    // these snake case names are used for the backend result data
    answersRef.current = [
      ...answersRef.current,
      {
        prompt: currentQuestion.prompt,
        roman: currentQuestion.roman,
        language_mode: quizLanguage,
        correct_key: currentQuestion.correctKey,
        correct_answer: currentQuestion.correctTitle,
        selected_key: selectedOptionKey,
        selected_answer:
          currentQuestion.options.find((option) => option.key === selectedOptionKey)?.title || "",
        was_correct: isCorrect,
      },
    ];

    setSelectedKey(selectedOptionKey);
    setShowFeedback(true);

    setQuestions((previousQuestions) =>
      previousQuestions.map((question, index) =>
        index === currentIndex
          ? { ...question, selectedKey: selectedOptionKey }
          : question
      )
    );
  }

  function handleNext() {
    setCurrentIndex((previousIndex) => previousIndex + 1);

    // only clear the feedback when there is another question waiting
    if (currentIndex < questions.length - 1) {
      setSelectedKey(null);
      setShowFeedback(false);
    }
  }

  function handlePlayAgain() {
    setHasStarted(false);
    resetQuizState();
  }

  useEffect(() => {
    async function saveQuizResult() {
      if (!isFinished || hasSavedResultRef.current) return;

      hasSavedResultRef.current = true;
      setSavingResult(true);
      playYaySound();

      const finalScore = scoreRef.current;
      const finalAnswers = answersRef.current;
      const playerName = getCurrentUserName();

      if (!playerName) {
        console.warn("[LanguagesQuiz] No player name, score not saved.");
        setSavingResult(false);
        return;
      }

      try {
        const payload = {
          name: playerName,
          page_name: pageName,
          score: finalScore,
          total_questions: questions.length,
          badge_key: "languages_master",
          answers: finalAnswers,
        };

        const response = await fetch(`${API_BASE}/api/quiz-result`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        let resultData = {};
        try {
          resultData = await response.json();
        } catch {
          resultData = {};
        }

        if (!response.ok) {
          console.error("[LanguagesQuiz] Save failed:", resultData);
          return;
        }

        if (resultData?.best_score !== null && resultData?.best_score !== undefined) {
          setBestScore(resultData.best_score);
        }

        // the popup only shows when the backend says this badge was just earned
        if (resultData?.newly_awarded_badge === true) {
          playBadgeSound();
          setTimeout(() => setShowBadgePopup(true), 500);
        }
      } catch (error) {
        console.error("[LanguagesQuiz] Network error:", error);
      } finally {
        setSavingResult(false);
      }
    }

    saveQuizResult();
  }, [isFinished, questions.length, pageName]);

  if (!hasStarted) {
    return (
      <div className="languagesQuizPage">
        <header className="languagesQuizHeader">
          <div className="languagesQuizHeaderTop">
            <HomeButton goToPage={goToPage} />
          </div>

          <h1 className="languagesQuizTitle">Languages Quiz Adventure!</h1>
          <p className="languagesQuizSubtitle">
            Choose Sinhala or Tamil, then match the word or number to English.
          </p>
        </header>

        <main className="languagesQuizWrap">
          <section className="introPanel">
            {bestScore !== null && (
              <p className="introBestScore">
                Your best score: <strong>{bestScore} / {TOTAL_QUESTIONS}</strong>
              </p>
            )}

            <h2 className="introTitle">Choose your quiz</h2>

            <div className="languageModeToggle" role="tablist" aria-label="Language mode">
              <button
                type="button"
                className={`languageModeBtn ${quizLanguage === LANGUAGE.SINHALA ? "active" : ""}`}
                onClick={() => setQuizLanguage(LANGUAGE.SINHALA)}
              >
                Sinhala
              </button>
              <button
                type="button"
                className={`languageModeBtn ${quizLanguage === LANGUAGE.TAMIL ? "active" : ""}`}
                onClick={() => setQuizLanguage(LANGUAGE.TAMIL)}
              >
                Tamil
              </button>
            </div>

            <div className="introSteps">
              <div className="introStep">
                <span className="introStepNumber">1</span>
                <p>Choose Sinhala or Tamil.</p>
              </div>
              <div className="introStep">
                <span className="introStepNumber">2</span>
                <p>Read the greeting or number shown on the screen.</p>
              </div>
              <div className="introStep">
                <span className="introStepNumber">3</span>
                <p>Tap the sound button to hear the word.</p>
              </div>
              <div className="introStep">
                <span className="introStepNumber">4</span>
                <p>Choose the correct English answer from 3 choices.</p>
              </div>
              <div className="introStep">
                <span className="introStepNumber">5</span>
                <p>Get 5 out of 5 to win the Languages Badge!</p>
              </div>
            </div>

            <div className="introActions">
              <button className="startQuizBtn" onClick={startQuiz} type="button">
                Start Quiz
              </button>
            </div>
          </section>
        </main>
      </div>
    );
  }

  if (isFinished) {
    const finalDisplayScore = questions.filter(
      (question) => question.selectedKey === question.correctKey
    ).length;

    return (
      <div className="languagesQuizPage">
        <header className="languagesQuizHeader">
          <div className="languagesQuizHeaderTop">
            <HomeButton goToPage={goToPage} />
          </div>

          <h1 className="languagesQuizTitle">Languages Quiz Adventure!</h1>
        </header>

        <main className="languagesQuizWrap">
          <section className="resultPanel">
            <img
              src={quizResultImg}
              alt="Quiz result"
              className="quizResultImage"
            />

            <h2 className="quizResultTitle">You finished the quiz!</h2>
            <p className="quizResultScore">
              Your score: <span>{finalDisplayScore}</span> / {questions.length}
            </p>

            {savingResult && <p className="quizSavingText">Saving your score...</p>}

            <div className="quizResultActions">
              <button className="quizActionBtn" onClick={handlePlayAgain} type="button">
                Play Again
              </button>
              <button
                className="quizActionBtn secondary"
                onClick={() => goToPage("/languages")}
                type="button"
              >
                Back to Languages Game
              </button>
            </div>
          </section>
        </main>

        {showBadgePopup && (
          <div className="badgeOverlay" onClick={() => setShowBadgePopup(false)}>
            <div
              className="badgePopup"
              onClick={(event) => event.stopPropagation()}
            >
              <h2 className="badgePopupTitle">Badge Unlocked!</h2>
              <p className="badgePopupText">
                You got 5 out of 5 and earned the Languages Badge!
              </p>
              <img src={languagesBadge} alt="Languages badge" className="badgeImage" />
              <button
                className="quizActionBtn"
                onClick={() => setShowBadgePopup(false)}
                type="button"
              >
                Yay!
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="languagesQuizPage">
      <header className="languagesQuizHeader">
        <div className="languagesQuizHeaderTop">
          <HomeButton goToPage={goToPage} />
        </div>

        <h1 className="languagesQuizTitle">Languages Quiz Adventure!</h1>
        <p className="languagesQuizSubtitle">
          {quizLanguage === LANGUAGE.SINHALA ? "Sinhala Quiz" : "Tamil Quiz"}
        </p>
      </header>

      <div className="languagesQuizTopBar">
        <span className="quizPill">
          Question: <b>{progressText}</b>
        </span>
        <span className="quizPill">
          Score: <b>{score}</b>
        </span>
      </div>

      <main className="languagesQuizWrap">
        <section className="questionPanel">
          <p className="quizPromptLabel">What does this mean?</p>

          <div className="languagesPromptPanel">
            <div className="languagesRomanText">{currentQuestion.roman}</div>
            <div className="languagesPromptWord">{currentQuestion.prompt}</div>

            <button
              type="button"
              className="listenBtn"
              onClick={() => playAudio(currentQuestion.audio)}
              aria-label="Listen"
              title="Listen"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 64 64"
                xmlns="http://www.w3.org/2000/svg"
                fill="#ffffff"
                style={{ display: "block", flexShrink: 0 }}
              >
                <path d="M59.998,28.001h-7.999c-2.211,0-4,1.789-4,4s1.789,4,4,4h7.999c2.211,0,4-1.789,4-4S62.209,28.001,59.998,28.001z"/>
                <path d="M49.71,19.466l6.929-4c1.914-1.105,2.57-3.551,1.461-5.465c-1.102-1.914-3.547-2.57-5.46-1.465l-6.93,4c-1.914,1.105-2.57,3.551-1.461,5.464C45.351,19.915,47.796,20.571,49.71,19.466z"/>
                <path d="M56.639,48.535l-6.929-3.999c-1.914-1.105-4.355-0.449-5.461,1.464c-1.105,1.914-0.453,4.359,1.461,5.465l6.93,4c1.913,1.105,4.358,0.449,5.464-1.465S58.553,49.641,56.639,48.535z"/>
                <path d="M37.53,0.307c-1.492-0.625-3.211-0.277-4.359,0.867L18.343,16.001H4c-2.211,0-4,1.789-4,4v24C0,46.211,1.789,48,4,48h14.343l14.828,14.828C33.937,63.594,34.96,64,35.999,64c0.516,0,1.035-0.098,1.531-0.305c1.496-0.617,2.469-2.078,2.469-3.695V4.001C39.999,2.384,39.026,0.924,37.53,0.307z"/>
              </svg>
              Listen
            </button>
          </div>

          <div className="languagesOptionsGrid">
            {currentQuestion.options.map((option) => {
              const isSelected = selectedKey === option.key;
              const isCorrect = showFeedback && option.key === currentQuestion.correctKey;
              const isWrong =
                showFeedback && isSelected && option.key !== currentQuestion.correctKey;

              return (
                <button
                  key={option.key}
                  className={`languagesOptionBtn ${isSelected ? "selected" : ""} ${isCorrect ? "correct" : ""} ${isWrong ? "wrong" : ""}`}
                  onClick={() => handleAnswer(option.key)}
                  type="button"
                  disabled={showFeedback}
                >
                  {option.title}
                </button>
              );
            })}
          </div>

          {showFeedback && (
            <div
              className={`quizFeedback ${
                selectedKey === currentQuestion.correctKey ? "good" : "bad"
              }`}
            >
              {selectedKey === currentQuestion.correctKey
                ? "Correct! Great job!"
                : `Oops! The correct answer is ${currentQuestion.correctTitle}.`}
            </div>
          )}

          {showFeedback && (
            <div className="quizNextRow">
              <button className="quizActionBtn" onClick={handleNext} type="button">
                {currentIndex === questions.length - 1 ? "See Results" : "Next Question"}
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}